import { NUSDIcon, WhiteCheckMarkIcon } from "../../../AssetComponents/Images";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, memo } from "react";
import { api } from "../../../Components/functions/Api";
import { useLocation, useNavigate } from "react-router-dom";
import { getTime, getDay } from "../../../Components/functions/Functions";
import { getProfileFromWallet } from "../../../Components/functions/Functions";
import Skeleton from "react-loading-skeleton";
import { updateTokenSafe } from "../../../Components/functions/Functions";
import { Mainnet } from "@nibiruchain/nibijs";
import { updateNFTStore } from "../../../ReduxSlices/NFTSlice";

// 🚀 PHASE 7: Memoize ChatItem to prevent unnecessary rerenders
export const ChatItem = memo(({
  myChatId,
  time,
  mode,
  setReceiver = () => {},
  setNft = () => {},
  setReceiverProfile = () => {},
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const account = useSelector((state) => state.auth.account);
  const dispatch = useDispatch();
  const [receiver, setReceiverAccount] = useState(null);
  const [latestMessageTime, setLatestTime] = useState(time);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [nftId, setNftId] = useState(null);
  const diff = useSelector((state) => state.time.diffToUTC);
  const [profile, setProfile] = useState();
  
  const [isLoadingNft, setIsLoadingNft] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [hasNoNftId, setHasNoNftId] = useState(false); // Track if nftId is confirmed missing/invalid
  
  const nft = useSelector((state) => state.nft.nfts[nftId]);

  const mainnet = Mainnet();
  const getToken = async () => {
    if (!nftId) {
      console.log("❌ ChatItem: No nftId provided");
      setHasNoNftId(true); // Mark as no NFT if no nftId
      return;
    }
    
    if (nft) {
      console.log("✅ ChatItem: Token already loaded:", nftId);
      return;
    }
    
    if (isLoadingNft) {
      console.log("🔄 ChatItem: Already loading token:", nftId);
      return;
    }
    
    console.log("🔄 ChatItem: Loading token:", nftId);
    
    try {
      setIsLoadingNft(true);
      // 🚀 INSTANT DASHBOARDS: Use backend API instead of RPC calls
      const propertyData = await api("property/getProperty", {
        token_id: nftId,
      });
      if (propertyData) {
        // 🔥 FIX: Validate metaData exists before storing in Redux
        if (!propertyData.metaData || typeof propertyData.metaData !== 'object') {
          console.warn("⚠️ ChatItem: Invalid metaData received from backend:", {
            nftId,
            hasMetaData: !!propertyData.metaData,
            metaDataType: typeof propertyData.metaData
          });
          setHasNoNftId(true);
          return;
        }
        
        // Update Redux with backend data (no RPC needed)
        dispatch(
          updateNFTStore({
            token_id: nftId,
            metaData: propertyData.metaData,
            short: propertyData.short,
            rentals: propertyData.rentals,
            access: propertyData.access,
          })
        );
        console.log("✅ ChatItem: Token loaded successfully from backend:", nftId);
      } else {
        // Fallback to RPC only if backend fails
        console.log("⚠️ ChatItem: Backend returned no data, falling back to RPC:", nftId);
        const success = await updateTokenSafe(
          {
            token_id: nftId,
            contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
          },
          mainnet.endptTm,
          dispatch
        );
        if (success) {
          console.log("✅ ChatItem: Token loaded successfully from RPC:", nftId);
        } else {
          console.log("❌ ChatItem: Failed to load token:", nftId);
          setHasNoNftId(true);
        }
      }
    } catch (error) {
      console.error("💥 ChatItem: Error loading token:", nftId, error);
      // Try RPC fallback on error
      try {
        await updateTokenSafe(
          {
            token_id: nftId,
            contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
          },
          mainnet.endptTm,
          dispatch
        );
      } catch (rpcError) {
        console.error("💥 ChatItem: RPC fallback also failed:", nftId, rpcError);
        setHasNoNftId(true);
      }
    } finally {
      setIsLoadingNft(false);
    }
  };

  // 🔥 FIX: Always load NFT for chat list preview (needs building name), but prioritize active chats
  // Only try to load if we have a valid nftId (not confirmed missing)
  useEffect(() => {
    if (nftId && !hasNoNftId && !nft && !isLoadingNft) {
      if (active) {
        // Load immediately if active (being viewed)
        getToken();
      } else {
        // Load NFT for inactive items too (needed for building name preview in list)
        // Use small delay to prioritize active chat loading, but still load for preview
        const delayTimer = setTimeout(() => {
          getToken();
        }, 200); // Small delay to not block active chat loading
        return () => clearTimeout(delayTimer);
      }
    }
  }, [active, nftId, hasNoNftId, nft, isLoadingNft]);
  
  // 🔥 NEW: Timeout to stop showing skeleton if NFT doesn't load within reasonable time
  useEffect(() => {
    if (nftId && !hasNoNftId && !nft && !isLoadingNft) {
      const timeout = setTimeout(() => {
        console.log("⚠️ ChatItem: NFT load timeout after 10s, setting hasNoNftId flag");
        setHasNoNftId(true);
      }, 10000); // 10 second timeout
      return () => clearTimeout(timeout);
    }
  }, [nftId, hasNoNftId, nft, isLoadingNft]);

  // 🔥 FIX: Improved profile loading
  const getProfile = async () => {
    if (!receiver || profile) return; // Don't fetch if we already have the data
    
    try {
      setIsLoadingProfile(true);
      const profileData = await getProfileFromWallet(receiver);
      setProfile(profileData);
    } catch (error) {
      console.error(`Failed to load profile for ${receiver}:`, error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (time !== null) {
      // FIX: Only update if new time is more recent
      setLatestTime((currentTime) => {
        if (!currentTime) return time; // No current time, use new time
        
        try {
          const newTime = new Date(time);
          const existingTime = new Date(currentTime);
          
          // Only update if new time is more recent
          if (newTime.getTime() > existingTime.getTime()) {
            console.log("ChatItem: Updating to newer time:", time);
            return time;
          } else {
            console.log("ChatItem: Keeping existing newer time:", currentTime, "vs", time);
            return currentTime;
          }
        } catch (error) {
          console.error("Error comparing times:", error);
          return time; // Fallback to new time on error
        }
      });
    }
  }, [time]);
  const setToActive = () => {
    const searchParams = new URLSearchParams(location.search);
    // 🔥 FIX: Clear all navigation params when switching to chat
    searchParams.delete("id");
    searchParams.delete("receiver");
    searchParams.delete("property");
    searchParams.delete("context");
    searchParams.delete("mode");
    searchParams.set("chat", myChatId);
    navigate(location.pathname + `?${searchParams.toString()}`);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setActive(searchParams.get("chat") === myChatId);
  }, [location.search, myChatId]);
  // 🔥 FIX: Enhanced chatting info loading with better error handling
  const getChattingInfo = async () => {
    if (!account || !myChatId) {
      console.log("❌ ChatItem: Missing account or chatId");
      return;
    }
    
    try {
      console.log("🔄 ChatItem: Getting chatting info for:", myChatId);
      const res = await api("chat/getChattingInfo", {
        id: myChatId,
        reader: account,
      });
      
      if (res) {
        console.log("✅ ChatItem: Chatting info received:", {
          receiver: res.receiver,
          nftId: res.nftId,
          unreadMessages: res.unreadMessages
        });
        
        setUnreadMessages(res.unreadMessages || 0);
        
        // 🔥 FIX: Better time handling
        if (res.latestMessageTime) {
          setLatestTime(res.latestMessageTime);
        }
        
        setReceiverAccount(res.receiver);
        
        // Only set nftId if it's valid
        if (res.nftId && res.nftId !== 'undefined' && res.nftId !== 'null') {
          setNftId(res.nftId);
          setHasNoNftId(false); // We have a valid nftId
        } else {
          console.log("⚠️ ChatItem: Invalid or missing nftId received:", res.nftId);
          setHasNoNftId(true); // Mark that there's no valid nftId
          setNftId(null); // Ensure nftId is null
        }
        
        // 🔥 FIX: Notify parent component about updates
        if (active) {
          setReceiver(res.receiver);
          if (res.nftId && res.nftId !== 'undefined' && res.nftId !== 'null') {
            setNft(res.nftId);
          }
        }
      } else {
        console.log("❌ ChatItem: No response from getChattingInfo");
      }
    } catch (error) {
      console.error("💥 ChatItem: Failed to get chatting info:", error);
    }
  };

  // 🔥 FIX: Better effect dependencies and timing
  useEffect(() => {
    if (myChatId && account) {
      getChattingInfo();
    }
  }, [account, myChatId]);

  // 🔥 FIX: Update parent when this chat becomes active
  useEffect(() => {
    if (active && receiver && nftId) {
      setReceiver(receiver);
      setNft(nftId);
      setReceiverProfile(profile);
    }
  }, [active, receiver, nftId, profile, setReceiver, setNft, setReceiverProfile]);

  // 🔥 FIX: Always load profile for chat list (needed for avatar/name), prioritize active chats
  useEffect(() => {
    if (receiver && !profile && !isLoadingProfile) {
      if (active) {
        // Load immediately if active
        getProfile();
      } else {
        // Load profile for inactive items too (needed for avatar/name in list)
        // Use small delay to prioritize active chat loading
        const delayTimer = setTimeout(() => {
          getProfile();
        }, 100); // Small delay to not block active chat loading
        return () => clearTimeout(delayTimer);
      }
    }
  }, [active, receiver, profile, isLoadingProfile]);

  // 🔥 FIX: Add data validation with null checks
  const hasValidNftData = nft && nft.metaData && nft.short;
  const pricePerDay = hasValidNftData ? nft.short.price_per_day : null;
  const buildingName = hasValidNftData ? nft.metaData.buildingName : null;
  const propertyImage = hasValidNftData && nft.metaData.images?.length > 0 ? nft.metaData.images[0] : null;

  // Debug logging for metadata issues
  useEffect(() => {
    if (nftId) {
      console.log("ChatItem NFT Data Debug:", {
        nftId,
        hasNft: !!nft,
        hasMetaData: !!(nft && nft.metaData),
        hasShort: !!(nft && nft.short),
        metaDataKeys: nft?.metaData ? Object.keys(nft.metaData) : 'No metaData',
        isLoadingNft
      });
    }
  }, [nft, nftId, isLoadingNft]);

  return (
    <div
      onClick={setToActive}
      className={
        active
          ? "cursor-pointer bg-gradient-to-b from-[#6B349A] from-0% to-70% to-[#4C37C3] p-[2px] rounded-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
          : "cursor-pointer bg-white p-[2px] rounded-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
      }
    >
      <div className="bg-white hover:bg-[#f6f6f6] rounded-[10px] w-full h-full p-[8px] sm:p-[12px]">
        <div className="w-full justify-between flex">
          <div className="flex items-center gap-[3px] sm:gap-[4px] w-max py-[3px] sm:py-[4px] px-[6px] sm:px-[8px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
            <div className="p-[3px] sm:p-[4px] rounded-full bg-[#5DBE89] w-max">
              <WhiteCheckMarkIcon className="w-[10px] sm:w-[12px]" />
            </div>
            <div className="text-[#5DBE89] text-[12px] sm:text-[14px]">Inquiry</div>
          </div>
          
          <div className="flex items-center gap-[3px] sm:gap-[4px]">
            <svg
              className="scale-75 sm:scale-100"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.41768 2.76525C9.86093 1.33875 13.8082 2.9745 15.2347 6.41775C16.6612 9.861 15.0254 13.8083 11.5822 15.2348C8.13893 16.6613 4.19168 15.0255 2.76518 11.5823C1.33943 8.139 2.97443 4.19175 6.41768 2.76525"
                stroke="url(#paint0_linear_457_25163)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.78906 5.98874V9.47699L11.5311 11.1487"
                stroke="url(#paint1_linear_457_25163)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_457_25163"
                  x1="9.00009"
                  y1="2.24976"
                  x2="9.00009"
                  y2="15.7502"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#6B349A" />
                  <stop offset="1" stopColor="#4C37C3" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_457_25163"
                  x1="10.1601"
                  y1="5.98874"
                  x2="10.1601"
                  y2="11.1487"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#6B349A" />
                  <stop offset="1" stopColor="#4C37C3" />
                </linearGradient>
              </defs>
            </svg>

            <div className="text-xs sm:text-sm">
              {latestMessageTime ? (
                (() => {
                  try {
                    const messageDate = new Date(latestMessageTime);
                    if (isNaN(messageDate.getTime())) {
                      return "Invalid Time";
                    }
                    return getDay(messageDate) + " " + getTime(messageDate);
                  } catch (error) {
                    console.error("Error formatting time:", error);
                    return "Invalid Time";
                  }
                })()
              ) : (
                <Skeleton count={1} height={16} width={80} />
              )}
            </div>
          </div>
        </div>
        
        <div className="flex gap-[6px] sm:gap-[10px] items-center">
          {!isLoadingProfile && profile?.Avatar ? (
            <div className="my-2 sm:my-3 p-[2px] w-[32px] h-[32px] sm:w-[40px] sm:h-[40px] rounded-full bg-gradient-to-b from-[#5b1deec0] from-0% to-70% to-[#5b1dee00]">
              <img
                alt="Avatar"
                className="w-full h-full rounded-full object-cover"
                src={profile.Avatar}
              />
            </div>
          ) : (
            <Skeleton
              width={32}
              height={32}
              style={{ borderRadius: "100px" }}
              className="sm:w-[40px] sm:h-[40px]"
            />
          )}
          
          {!isLoadingProfile && profile?.Name ? (
            <div className="text-sm sm:text-base truncate flex-1">
              {profile.Name.replace("/", " ")}
            </div>
          ) : (
            <Skeleton count={1} height={16} width={80} />
          )}
        </div>

        <div className="flex justify-between w-full items-end">
          <div className="flex-1 min-w-0">
            {hasNoNftId ? (
              // Show empty space when no NFT data (e.g., traveler without listings)
              <div className="font-normal text-[12px] sm:text-[14px] truncate">
                {/* Empty space - no property information */}
              </div>
            ) : !isLoadingNft && hasValidNftData && nft.metaData.buildingName ? (
              <div className="font-normal text-[12px] sm:text-[14px] truncate">
                {nft.metaData.buildingName}
              </div>
            ) : isLoadingNft ? (
              <>
                <Skeleton height={8} width="80%" />
                <Skeleton height={8} width="60%" />
              </>
            ) : (
              // Empty space when loading finished but no data
              <div className="font-normal text-[12px] sm:text-[14px] truncate">
                {/* Empty space - no property data */}
              </div>
            )}

            <div className="flex items-center gap-[3px] sm:gap-[4px] mt-1 sm:mt-2">
              <div className="scale-75 sm:scale-100">
                <NUSDIcon />
              </div>
              {hasNoNftId ? (
                // Empty space for price when no NFT
                <div className="text-xs sm:text-sm"></div>
              ) : !isLoadingNft && pricePerDay !== null && pricePerDay !== undefined ? (
                <div className="text-xs sm:text-sm">{pricePerDay} USDC</div>
              ) : isLoadingNft ? (
                <Skeleton height={16} width={60} />
              ) : (
                // Empty space when loading finished but no data
                <div className="text-xs sm:text-sm"></div>
              )}
            </div>
          </div>
          
          {hasNoNftId ? (
            // Empty space when no NFT - don't show placeholder
            null
          ) : !isLoadingNft && hasValidNftData && nft.metaData.images?.[0] ? (
            <img
              alt="Property"
              src={nft.metaData.images[0]}
              className="rounded-[12px] w-[50px] h-[36px] sm:w-[60px] sm:h-[44px] object-cover flex-shrink-0"
            />
          ) : isLoadingNft ? (
            <Skeleton 
              height={36} 
              width={50} 
              style={{ borderRadius: "12px" }}
              className="sm:h-[44px] sm:w-[60px]"
            />
          ) : (
            // Empty space when loading finished but no data - don't show placeholder
            null
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // 🚀 PHASE 7: Custom comparison to prevent unnecessary rerenders
  // Only rerender if these props change
  return (
    prevProps.myChatId === nextProps.myChatId &&
    prevProps.time === nextProps.time &&
    prevProps.mode === nextProps.mode
  );
});

export const SentMessage = ({ text, image, messageType, time }) => {
  const displayTime = time || getTime(new Date());

  return (
    <div className="space-y-[4px] sm:space-y-[5px] my-[8px] sm:my-[10px] w-full">
      <div className="w-full flex justify-end">
        <div className="px-[8px] sm:px-[12px] py-[6px] sm:py-[8px] rounded-[12px] rounded-br-[0px] text-white bg-gradient-to-b from-[#5b1deec0] from-0% to-70% to-[#5b1dee] break-words max-w-[300px] sm:max-w-[600px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] text-sm sm:text-base">
          {/* Image display */}
          {image && (
            <div className="mb-2 rounded-[8px] overflow-hidden">
              <img
                src={image}
                alt="Shared image"
                className="max-w-full h-auto max-h-[400px] object-contain rounded-[8px] cursor-pointer"
                onClick={() => window.open(image, '_blank')}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                }}
              />
            </div>
          )}
          {/* Text display */}
          {text && text.trim() && (
            <div className={image ? "mt-2" : ""}>
              {text}
            </div>
          )}
        </div>
      </div>
      <div className="w-full flex justify-end gap-[3px] sm:gap-[4px] items-center">
        <div className="text-[#B6B6B6] text-xs sm:text-sm">{displayTime}</div>
        <svg 
          className="scale-75 sm:scale-100"
          width="20" 
          height="20" 
          viewBox="0 0 20 20" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3.75 10.7209L6.18327 12.9605C6.49151 13.2442 6.96906 13.2328 7.26341 12.9347L13.2879 6.83392" stroke="#B6B6B6" strokeWidth="1.3" strokeLinecap="round"/>
          <path d="M16.2474 6.83392L10.3828 12.6986" stroke="#B6B6B6" strokeWidth="1.3" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );
};

export const ReceivedMessage = ({ text, image, messageType, time }) => {
  const displayTime = time || getTime(new Date());

  return (
    <div className="space-y-[4px] sm:space-y-[5px] my-[8px] sm:my-[10px] w-full">
      <div className="w-full flex justify-start">
        <div className="px-[8px] sm:px-[12px] py-[6px] sm:py-[8px] rounded-[12px] rounded-bl-[0px] bg-white break-words max-w-[300px] sm:max-w-[600px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] text-sm sm:text-base">
          {/* Image display */}
          {image && (
            <div className="mb-2 rounded-[8px] overflow-hidden">
              <img
                src={image}
                alt="Shared image"
                className="max-w-full h-auto max-h-[400px] object-contain rounded-[8px] cursor-pointer"
                onClick={() => window.open(image, '_blank')}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                }}
              />
            </div>
          )}
          {/* Text display */}
          {text && text.trim() && (
            <div className={image ? "mt-2" : ""}>
              {text}
            </div>
          )}
        </div>
      </div>
      <div className="w-full flex justify-start gap-[6px] sm:gap-[10px] items-center">
        <div className="text-[#B6B6B6] text-xs sm:text-sm">{displayTime}</div>
      </div>
    </div>
  );
};